/**
 * MsgBubble + ReadTick + TypingIndicator — TeamTalk (V1.7.9 refactor).
 * V1.8.5 — reactions (long-press/dbl-click picker).
 * V1.9.27 — WhatsApp-style bubble polish + swipe-to-reply.
 */
import React from "react";
import { Check, Checks } from "@phosphor-icons/react";
import { formatWhen } from "./helpers";
import AttachmentBubble from "./AttachmentBubble";
import {
    ReactionBar,
    ReactionPicker,
    QuickReactionsHoverStrip,
    useLongPress,
} from "@/components/chat/ReactionPicker";
import {
    useSwipeToReply,
    ReplyQuote,
    SwipeReplyIndicator,
} from "@/components/chat/SwipeToReply";

export function MsgBubble({
    m, isMine, senderName, showSenderLabel, readReceipt,
    senderAvatarUrl, showAvatar,
    myUserId, onReact, onReply, onForward, onStar, replyQuoted, onJumpToQuote,
}) {
    const isSystem = m.sender_role === "system";
    const [pickerOpen, setPickerOpen] = React.useState(false);
    const longPress = useLongPress(() => setPickerOpen(true));
    const isStarred = !!(myUserId && (m.starred_by_user_ids || []).includes(myUserId));
    const swipe = useSwipeToReply({
        onReply: () => onReply?.(m),
        onForward: () => onForward?.(m),
        isMine,
        disabled: (!onReply && !onForward) || isSystem,
    });
    const handleReactPick = (emoji) => {
        setPickerOpen(false);
        onReact?.(m.id, emoji);
    };
    const handleChipToggle = (emoji) => onReact?.(m.id, emoji);

    if (isSystem) {
        return (
            <div className="flex justify-center">
                <span className="text-[10px] text-ink/60 italic bg-white px-3 py-1 rounded-lg shadow-sm">{m.body}</span>
            </div>
        );
    }
    const att = m.attachment;
    const isVoice = att && (att.kind === "voice" || (att.content_type || "").startsWith("audio/"));

    return (
        <div className={`flex ${isMine ? "justify-end" : "justify-start"} gap-2 relative`}>
            {/* Peer avatar column (WhatsApp Web style) — only for groups */}
            {!isMine && showAvatar !== undefined && (
                <div className="w-8 shrink-0 flex items-end">
                    {showAvatar ? (
                        senderAvatarUrl ? (
                            <img
                                src={senderAvatarUrl}
                                alt={senderName || "Teammate"}
                                data-testid={`teamtalk-bubble-avatar-${m.id}`}
                                className="w-8 h-8 rounded-full object-cover ring-1 ring-ink/10"
                            />
                        ) : (
                            <div
                                data-testid={`teamtalk-bubble-avatar-initial-${m.id}`}
                                className="w-8 h-8 rounded-full bg-gradient-to-br from-cobalt to-cobalt/70 text-white flex items-center justify-center text-[11px] font-bold"
                            >
                                {(senderName || "T").trim().charAt(0).toUpperCase()}
                            </div>
                        )
                    ) : (
                        <div className="w-8 h-8" aria-hidden="true" />
                    )}
                </div>
            )}

            {/* Swipe indicator sits in the row track, appears as bubble drags */}
            <SwipeReplyIndicator progress={swipe.progress} isMine={isMine} side={swipe.swipeSide} />

            <div
                ref={swipe.wrapRef}
                className={`group flex flex-col ${isMine ? "items-end" : "items-start"} max-w-[85%] relative`}
                style={{
                    touchAction: "pan-y",
                }}
                {...swipe.handlers}
            >
                <QuickReactionsHoverStrip
                    onPick={handleReactPick}
                    onStar={onStar ? () => onStar(m.id) : undefined}
                    isStarred={isStarred}
                    isMine={isMine}
                    testIdPrefix={`teamtalk-quick-react-${m.id}`}
                />
                <div
                    {...longPress}
                    onDoubleClick={() => setPickerOpen(true)}
                    data-testid={`teamtalk-bubble-${m.id}`}
                    className={`relative px-2.5 py-1.5 text-[14px] leading-[19px] shadow-sm select-none min-w-0 max-w-full overflow-hidden ${
                        isMine
                            ? "bg-[#d9fdd3] text-ink rounded-2xl rounded-br-md"
                            : "bg-white text-ink rounded-2xl rounded-bl-md"
                    }`}
                    style={{ minWidth: "72px" }}
                >
                    {showSenderLabel && (
                        <p className="text-[11px] font-bold mb-0.5 text-cobalt">
                            {senderName || "Teammate"}
                        </p>
                    )}

                    {/* Quoted reply header, WhatsApp-style vertical accent */}
                    {m.reply_to_message_id && replyQuoted && (
                        <ReplyQuote
                            quoted={replyQuoted}
                            isMine={isMine}
                            onJump={onJumpToQuote ? () => onJumpToQuote(m.reply_to_message_id) : undefined}
                        />
                    )}

                    {/* Body — inline span so the floated meta wraps naturally
                        (WhatsApp trick). Block <p> would push meta to a new line. */}
                    {m.body && !isVoice && (
                        <span className="whitespace-pre-wrap break-words">{m.body}</span>
                    )}

                    {/* Attachments (image/video/voice/doc) */}
                    {att && (
                        <div className={isVoice ? "" : "mt-1"}>
                            <AttachmentBubble att={att} messageId={m.id} isMine={isMine} />
                        </div>
                    )}
                    {/* Optional caption body under an attachment */}
                    {att && m.body && !isVoice && (
                        <span className="whitespace-pre-wrap break-words mt-1 block">{m.body}</span>
                    )}

                    {/* Inline meta anchored bottom-right (WhatsApp trick):
                        floats and clears so it snugs the last line of text. */}
                    <span
                        className="float-right ml-2 mt-1 text-[10px] leading-none text-ink/45 whitespace-nowrap inline-flex items-center gap-1 pt-0.5 pl-1 select-none"
                        data-testid={`teamtalk-bubble-meta-${m.id}`}
                    >
                        {isStarred && <span className="text-amber-500" data-testid={`teamtalk-star-${m.id}`}>★</span>}
                        {formatWhen(m.created_at)}
                        {isMine && readReceipt && <ReadTick receipt={readReceipt} />}
                    </span>
                    <span className="clear-both block" />

                    <ReactionPicker
                        open={pickerOpen}
                        onDismiss={() => setPickerOpen(false)}
                        onPick={handleReactPick}
                        onStar={onStar ? () => { setPickerOpen(false); onStar(m.id); } : undefined}
                        isStarred={isStarred}
                        anchor="top"
                    />
                </div>
                <ReactionBar
                    reactions={m.reactions}
                    myUserId={myUserId}
                    onToggle={handleChipToggle}
                    testIdPrefix={`teamtalk-reaction-${m.id}`}
                />
            </div>
        </div>
    );
}

function ReadTick({ receipt }) {
    if (!receipt || receipt.total === 0) return null;
    if (!receipt.seen) {
        return (
            <span data-testid="teamtalk-read-tick-sent" title="Sent" className="inline-flex items-center text-ink/45">
                <Check size={13} weight="bold" />
            </span>
        );
    }
    const title = receipt.isGroup
        ? `Seen by ${receipt.seenBy.slice(0, 6).join(", ")}${receipt.seenBy.length > 6 ? "…" : ""}`
        : "Seen";
    return (
        <span
            data-testid={receipt.seenAll ? "teamtalk-read-tick-seen" : "teamtalk-read-tick-partial"}
            title={title}
            className={`inline-flex items-center gap-0.5 ${receipt.seenAll ? "text-sky-500" : "text-ink/50"}`}
        >
            <Checks size={14} weight="bold" />
            {receipt.isGroup && !receipt.seenAll && (
                <span className="text-[9px] font-black tabular-nums">{receipt.seenBy.length}/{receipt.total}</span>
            )}
        </span>
    );
}

export function TypingIndicator({ typingByUser, myUserId }) {
    const names = Object.entries(typingByUser)
        .filter(([uid]) => uid !== myUserId)
        .map(([, v]) => v.user_name);
    if (names.length === 0) return null;
    const label = names.length === 1
        ? `${names[0]} is typing`
        : names.length === 2
        ? `${names[0]} and ${names[1]} are typing`
        : `${names[0]} and ${names.length - 1} others are typing`;
    return (
        <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm w-fit" data-testid="teamtalk-typing-indicator">
            <span className="flex gap-1 items-end h-3">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
            </span>
            <span className="text-[11px] text-ink/70">{label}</span>
        </div>
    );
}
